import { useTranslation } from 'react-i18next';
import { LocationPindrop, TableNoData } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { InlineMessageSeverity } from '../../../stores/types/inlineMessageBanner.interface';
import { ColorType, Icon, InlineMessage, Label, LabelType, Spinner } from '../../_ui';
import { NearbyChargerResultCard } from './NearbyChargerResultCard.component';
import { FinderResult } from './types';
import { NearbySearchState } from './useNearbyChargerSearch';
import { MAX_SEARCH_RADIUS_KM, RESULT_LIMIT } from './utils/search';

interface NearbyChargerResultsProps {
  searchState: NearbySearchState;
  /** Results after every filter, already limited to RESULT_LIMIT */
  results: FinderResult[];
  activeId: number | null;
  primaryColor: string;
  onSelect: (poiId: number) => void;
  cardRef: (poiId: number, element: HTMLDivElement | null) => void;
}

const CenteredMessage = ({
  icon,
  iconColor,
  title,
  description,
}: {
  icon: string;
  iconColor?: ColorType;
  title?: string;
  description: string;
}) => (
  <div className='flex flex-col items-center justify-center gap-2 h-full text-center px-6'>
    <Icon src={icon} size={IconSize.SIZE_36x36} color={iconColor} />
    {title && <Label text={title} type={LabelType.LABEL_M} color={ColorType.BLACK} />}
    <Label text={description} type={LabelType.BODY3} color={ColorType.GREY5} />
  </div>
);

export const NearbyChargerResults = ({
  searchState,
  results,
  activeId,
  primaryColor,
  onSelect,
  cardRef,
}: NearbyChargerResultsProps) => {
  const { t } = useTranslation();

  switch (searchState.phase) {
    case 'idle':
      return (
        <CenteredMessage
          icon={LocationPindrop}
          iconColor={ColorType.GREY5}
          description={t('nearby_chargers_idle', { total: RESULT_LIMIT })}
        />
      );
    case 'loading':
      return (
        <div className='flex flex-col items-center justify-center gap-3 h-full'>
          <Spinner height='h-8' width='w-8' />
          <Label
            text={t('nearby_chargers_searching', { radius: searchState.radiusKm })}
            type={LabelType.BODY3}
            color={ColorType.GREY5}
          />
        </div>
      );
    case 'error':
      return (
        <InlineMessage
          severity={InlineMessageSeverity.ALERT}
          leadMessage={t(searchState.messageKey)}
          dataTestId='nearby-chargers-error'
        />
      );
    default:
      break;
  }

  if (!results.length) {
    return (
      <CenteredMessage
        icon={TableNoData}
        title={t('nearby_chargers_empty_title', { radius: MAX_SEARCH_RADIUS_KM })}
        description={t('nearby_chargers_empty_desc')}
      />
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-col'>
        <Label
          text={t(
            results.length === 1 ? 'nearby_chargers_found_one' : 'nearby_chargers_found_many',
            { total: results.length },
          )}
          type={LabelType.LABEL_S}
          color={ColorType.BLACK}
        />
        <Label
          text={t('nearby_chargers_found_near', { address: searchState.origin.label })}
          type={LabelType.BODY3}
          color={ColorType.GREY5}
        />
      </div>
      {results.map((result, index) => (
        <NearbyChargerResultCard
          key={result.poi.ID}
          result={result}
          rank={index + 1}
          isActive={activeId === result.poi.ID}
          primaryColor={primaryColor}
          onSelect={onSelect}
          cardRef={cardRef}
        />
      ))}
    </div>
  );
};
